/**
 * 「회사 관리」 카탈로그 — /manage/:category/:section 의 정본.
 *
 * 통합 전에는 관리자용 화면이 세 곳(ERP /admin 7탭 · HR /hr/admin/settings 23섹션 ·
 * HR /hr/admin 2탭)에 흩어져 있었다. 여기 한 배열로 모은다.
 *
 * ⛔ 화면 컴포넌트 파일은 **한 개도 옮기지 않는다.** 여기서 import 만 한다.
 *    ERP 탭 파일(pages/admin/tabs/*)은 HR 설정에서도 이미 import 해 쓰고 있어서,
 *    파일을 옮기면 양쪽이 동시에 깨진다. 입구만 하나로 줄이는 것이 이번 작업이다.
 *
 * 서랍 순서 원칙: 자주 만지고 덜 위험한 것 → 가끔 만지고 더 위험한 것.
 *   사람과 조직 → 접근 권한 → 회사 규칙 → 급여·복지 → 평가·성장 → 문서·계약 → 기록과 통계 → 시스템
 *   («접근 권한»만 예외로 앞에 둔다 — 가장 위험하지만 계정 관리 바로 옆에 있어야 찾는다.)
 *
 * 섹션을 추가할 때 손댈 곳은 이 파일 하나다. 좌측 목록·검색·라우팅·브레드크럼이 자동으로 따라온다.
 */

import {
  Users,
  KeyRound,
  ScrollText,
  Wallet,
  TrendingUp,
  FileText,
  BarChart3,
  Wrench,
} from 'lucide-react';
import { lazyWithRetry } from '../../utils/lazyWithRetry';
import type { SettingsCategory } from '../../components/settings/types';

/* ── 사람과 조직 ─────────────────────────────── */
const SignupApprovalsSection = lazyWithRetry(
  () => import('../../components/settings/SignupApprovalsSection'),
);
const AccountsSection = lazyWithRetry(() => import('./sections/AccountsSection'));
const TeamManagementSection = lazyWithRetry(() => import('./sections/TeamManagementSection'));
const ResponsibilityMatrixTab = lazyWithRetry(() => import('../admin/tabs/ResponsibilityMatrixTab'));
const LifecycleAdminPage = lazyWithRetry(() => import('../hr/admin/LifecycleAdminPage'));

/* ── 접근 권한 ───────────────────────────────── */
const MenuPermissionsTab = lazyWithRetry(() => import('../admin/tabs/MenuPermissionsTab'));
const PermissionPresetsTab = lazyWithRetry(() => import('../admin/tabs/PermissionPresetsTab'));

/* ── 회사 규칙 ───────────────────────────────── */
const LeaveTypesPage = lazyWithRetry(() => import('../hr/admin/LeaveTypesPage'));
const WorkPolicyPanel = lazyWithRetry(() => import('../hr/admin/settings/panels/WorkPolicyPanel'));
const ApprovalTemplatesAdminPage = lazyWithRetry(() => import('../hr/admin/ApprovalTemplatesAdminPage'));
const DelegationsAdminPage = lazyWithRetry(() => import('../hr/admin/DelegationsAdminPage'));
const IntegrationsAdminPage = lazyWithRetry(() => import('../hr/admin/IntegrationsAdminPage'));

/* ── 급여·복지 ───────────────────────────────── */
const PayrollPolicyPanel = lazyWithRetry(() => import('../hr/admin/settings/panels/PayrollPolicyPanel'));
const CompensationAdminPage = lazyWithRetry(() => import('../hr/admin/CompensationAdminPage'));
const InsurancePolicyPanel = lazyWithRetry(() => import('../hr/admin/settings/panels/InsurancePolicyPanel'));

/* ── 평가·성장 ───────────────────────────────── */
const GrowthAdminPage = lazyWithRetry(() => import('../hr/admin/GrowthAdminPage'));
const OkrPolicyPanel = lazyWithRetry(() => import('../hr/admin/settings/panels/OkrPolicyPanel'));

/* ── 문서·계약 ───────────────────────────────── */
const DocumentTemplatesAdminPage = lazyWithRetry(() => import('../hr/admin/DocumentTemplatesAdminPage'));
const CompanyDocumentsAdminPage = lazyWithRetry(() => import('../hr/admin/CompanyDocumentsAdminPage'));
const ContractsAdminPage = lazyWithRetry(() => import('../hr/admin/ContractsAdminPage'));

/* ── 기록과 통계 ─────────────────────────────── */
const InsightsAdminPage = lazyWithRetry(() => import('../hr/admin/InsightsAdminPage'));
const DocumentIssuancesAdminPage = lazyWithRetry(() => import('../hr/admin/DocumentIssuancesAdminPage'));
const ApprovalAnalyticsAdminPage = lazyWithRetry(() => import('../hr/admin/ApprovalAnalyticsAdminPage'));
const MonthlyReportsAdminPage = lazyWithRetry(() => import('../hr/admin/MonthlyReportsAdminPage'));
const SlackDispatchAdminPage = lazyWithRetry(() => import('../hr/admin/SlackDispatchAdminPage'));

/* ── 시스템 ──────────────────────────────────── */
const HealthCheckAdminPage = lazyWithRetry(() => import('../hr/admin/HealthCheckAdminPage'));
const ApiKeyManagementTab = lazyWithRetry(() => import('../admin/tabs/ApiKeyManagementTab'));
const CompanyAiPromptSection = lazyWithRetry(() => import('./sections/CompanyAiPromptSection'));
const ApiDocsPage = lazyWithRetry(() => import('../settings/ApiDocsPage'));
const DesignSystemPage = lazyWithRetry(() => import('../admin/DesignSystemPage'));

export const MANAGE_CATALOG: SettingsCategory[] = [
  {
    key: 'people',
    label: '사람과 조직',
    icon: Users,
    description: '계정 · 팀 · 업무분장',
    sections: [
      {
        key: 'signup-approvals',
        label: '가입 승인',
        description: '새로 가입한 사람을 승인하거나 거부',
        component: SignupApprovalsSection,
        keywords: ['가입', '승인', '신규', '대기', '거부', '입사'],
      },
      {
        key: 'accounts',
        label: '계정 관리',
        description: '직원 계정 생성·수정·비밀번호 초기화 · Slack 계정 연결',
        component: AccountsSection,
        // ⛔ «계정 연결» 을 여기 탭으로 합쳤다(2026-08-21). 옛 검색어를 남겨 둔다 —
        //    지우면 「슬랙 연결」로 검색하는 사람이 아무것도 못 찾는다.
        keywords: ['사용자', '유저', '직원', '가입', '비밀번호',
                   '슬랙', 'Slack', 'DM', '연동', '짝', '계정 연결'],
        // ⛔ 옛 주소를 여기서 받는다. 안 적으면 서랍 첫 항목(가입 승인)으로 떨어진다.
        aliases: { 'account-links': '?tab=links' },
      },
      {
        key: 'teams',
        label: '팀·소속 편집',
        description: '팀 이름·상위 팀·정렬 순서·겸직 팀원',
        component: TeamManagementSection,
        keywords: ['조직도', '부서', '소속', '겸직'],
      },
      {
        key: 'responsibility',
        label: '업무분장',
        description: '누가 어떤 업무를 맡는지',
        component: ResponsibilityMatrixTab,
        keywords: ['R&R', '역할', '담당'],
      },
      {
        key: 'lifecycle',
        label: '입·퇴사 체크리스트',
        description: '온보딩·오프보딩 템플릿과 일괄 실행',
        component: LifecycleAdminPage,
        keywords: ['온보딩', '오프보딩', '입사', '퇴사'],
      },
    ],
  },
  {
    key: 'access',
    label: '접근 권한',
    icon: KeyRound,
    description: '누가 무엇을 볼 수 있는가',
    sections: [
      {
        key: 'menu',
        label: '메뉴 권한',
        description: '계정별 ERP·인사 메뉴 접근 권한',
        component: MenuPermissionsTab,
        keywords: ['권한', '접근', '허용', '차단'],
        // AI 화이트리스트 화면을 없앴다(대표 지시 2026-08-21). 옛 즐겨찾기를 여기서 받는다.
        aliases: { 'ai-whitelist': '' },
      },
      {
        key: 'presets',
        label: '권한 프리셋',
        description: '팀별·직위별 권한 묶음',
        component: PermissionPresetsTab,
        keywords: ['템플릿', '권한묶음'],
      },
    ],
  },
  {
    key: 'rules',
    label: '회사 규칙',
    icon: ScrollText,
    description: '휴가 · 근무 · 결재',
    sections: [
      {
        key: 'leave-types',
        label: '휴가 정책',
        description: '법정·맞춤 휴가 유형과 부여 규칙',
        component: LeaveTypesPage,
        keywords: ['연차', '반차', '휴가'],
      },
      {
        key: 'work-policy',
        label: '근무 정책',
        description: '근무시간·휴게·연차 산정 기준',
        component: WorkPolicyPanel,
        keywords: ['출퇴근', '유연근무', '근태', '지각'],
      },
      {
        key: 'approval-templates',
        label: '결재 양식',
        description: '결재 카테고리와 결재선 템플릿',
        component: ApprovalTemplatesAdminPage,
        keywords: ['전자결재', '승인', '결재선'],
      },
      {
        key: 'delegations',
        label: '결재 위임',
        description: '자리를 비울 때 대신 결재할 사람 · 퇴직·휴직자의 결재는 자동 인계',
        component: DelegationsAdminPage,
        keywords: ['대결', '위임', '부재'],
      },
      {
        // ⛔ 라벨이 사실과 달랐다(2026-08-21 확인). 「설정 저장만」이라고 적혀 있었지만
        //    실제로는 **Slack 알림 발송을 켜고 끄는 스위치**로 쓰인다 —
        //    slackGate.js 가 hr_integration_settings.slack.is_enabled 를 읽고,
        //    발송 경로 3곳(slackHrNotifier · slackDmSender · integrations)이 그것을 탄다.
        //    지우면 관리자가 Slack 알림을 끌 방법이 없어진다.
        key: 'integrations',
        // ⛔ 라벨에 사실을 적는다. 감춰 두면 옛 즐겨찾기가 엉뚱한 곳으로 떨어져
        //    왜 사라졌는지 알 수 없고, 검색으로도 못 찾는다.
        // ⛔ 「Slack 알림 연동」 이라고만 쓰면 안 된다 — 화면에는 캘린더·이메일 스위치도 있고
        //    그 둘은 켜도 아무 동작을 하지 않는다 (2026-08-21 실측).
        label: '외부 연동',
        description: 'Slack 알림만 실제로 켜고 꺼집니다 · 캘린더·이메일은 값만 저장됩니다',
        component: IntegrationsAdminPage,
        keywords: ['슬랙', 'Slack', '캘린더', '연동', '통합', 'webhook'],
      },
    ],
  },
  {
    key: 'pay',
    label: '급여·복지',
    icon: Wallet,
    description: '규칙을 정하고 바로 아래에서 처리',
    sections: [
      {
        key: 'payroll-policy',
        label: '급여 정책',
        description: '급여 항목·지급일 규칙',
        component: PayrollPolicyPanel,
        keywords: ['월급', '지급일', '수당'],
      },
      {
        key: 'compensation',
        label: '급여·복지·보험 처리',
        description: '급여 정산 · 복지 승인 · 보험 등록',
        component: CompensationAdminPage,
        keywords: ['정산', '복지', '보험', '승인'],
      },
      {
        key: 'insurance-policy',
        label: '보험 정책',
        description: '단체보험·산재 규칙',
        component: InsurancePolicyPanel,
        keywords: ['산재', '단체보험', '4대보험'],
      },
    ],
  },
  {
    key: 'growth',
    label: '평가·성장',
    icon: TrendingUp,
    description: '목표 · 리뷰 · 교육',
    sections: [
      {
        key: 'cycles',
        label: '평가·목표·교육',
        description: '분기 리뷰 사이클 · 목표(OKR) · 교육 과정',
        component: GrowthAdminPage,
        keywords: ['성과', '평가', '리뷰', '교육', 'OKR'],
      },
      {
        key: 'okr-policy',
        label: 'OKR 정책',
        description: '분기 자동 생성·정렬 규칙',
        component: OkrPolicyPanel,
        keywords: ['목표', '분기'],
      },
    ],
  },
  {
    key: 'docs',
    label: '문서·계약',
    icon: FileText,
    description: '양식 · 공용 문서 · 계약 발행',
    sections: [
      {
        key: 'templates',
        label: '문서 양식',
        description: '증명서·회사 문서 템플릿',
        component: DocumentTemplatesAdminPage,
        keywords: ['서식', '템플릿', '증명서'],
      },
      {
        key: 'company',
        label: '회사 공용 문서',
        description: '전 직원이 함께 보는 문서',
        component: CompanyDocumentsAdminPage,
        keywords: ['사규', '규정', '공지'],
      },
      {
        key: 'contracts',
        label: '계약 발행',
        description: '근로·위탁 계약서 발행',
        component: ContractsAdminPage,
        keywords: ['근로계약', '위탁', '서명'],
      },
    ],
  },
  {
    key: 'reports',
    label: '기록과 통계',
    icon: BarChart3,
    description: '값을 정하지 않고 결과만 보는 화면',
    sections: [
      {
        key: 'insights',
        label: '인사 현황',
        description: '인원·근태·휴가 한눈에',
        component: InsightsAdminPage,
        keywords: ['대시보드', '현황', '인사이트'],
        // 협업 관계 그림을 없앴다(대표 지시 2026-08-21). 옛 즐겨찾기를 여기서 받는다.
        aliases: { 'relationship-graph': '' },
      },
      {
        key: 'issuances',
        label: '증명서 발급 이력',
        description: '재직·경력 증명 발급 기록',
        component: DocumentIssuancesAdminPage,
        keywords: ['재직증명', '경력증명', '발급'],
      },
      {
        key: 'approval-analytics',
        label: '결재 분석',
        description: '결재 흐름과 병목',
        component: ApprovalAnalyticsAdminPage,
        keywords: ['결재', '병목', '통계'],
      },
      {
        key: 'monthly',
        label: '월간 리포트',
        description: '매월 1일 04:30(KST) 전월 데이터 자동 집계 · 수동 생성과 PDF 내려받기',
        component: MonthlyReportsAdminPage,
        keywords: ['월간', 'PDF', '보고서'],
      },
      {
        key: 'slack-dispatch',
        label: 'Slack 발송 이력',
        description: '채널 라우팅 규칙과 발송 기록',
        component: SlackDispatchAdminPage,
        keywords: ['슬랙', '알림', '발송'],
      },
    ],
  },
  {
    key: 'system',
    label: '시스템',
    icon: Wrench,
    description: '개발·운영용',
    sections: [
      {
        key: 'health',
        label: '시스템 상태',
        description: '외부 연결·스케줄러·저장소 점검판 · 5분마다 자동 새로고침',
        component: HealthCheckAdminPage,
        keywords: ['헬스체크', '상태', '점검', '모니터링'],
      },
      {
        key: 'api-keys',
        label: 'API 키',
        description: '외부 연동용 키 발급·폐기',
        component: ApiKeyManagementTab,
        keywords: ['토큰', '연동', '키'],
      },
      {
        key: 'ai-prompts',
        label: 'AI 프롬프트',
        description: '회사 전체가 함께 쓰는 AI 어시스턴트 지시문',
        component: CompanyAiPromptSection,
        keywords: ['AI', '어시스턴트', '지시문', '시스템프롬프트'],
      },
      {
        key: 'api-docs',
        label: 'API 문서',
        description: '백엔드 엔드포인트 목록',
        component: ApiDocsPage,
        keywords: ['개발', '엔드포인트', '레퍼런스'],
      },
      {
        key: 'design-system',
        label: '디자인 시스템',
        description: '화면 부품 목록과 사용 예',
        component: DesignSystemPage,
        keywords: ['UI', '컴포넌트', '토큰'],
      },
    ],
  },
];

export const MANAGE_BASE = '/manage';
export const MANAGE_DEFAULT_PATH = `${MANAGE_BASE}/people/accounts`;
